import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Check,
  MapPin,
  MessageSquare,
  Target,
  User,
  X,
  Loader2,
  Inbox,
  Flag,
  ShieldCheck,
  Send,
} from "lucide-react";

import {
  usePendingContactRequests,
  useResendGuardianEmail,
  useRespondToContactRequest,
} from "../../hooks/queries/useContactRequests";
import ReportModal from "../scouting/ReportModal";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const InfoLine = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon size={16} className="mt-0.5 flex-shrink-0 text-blue-950" />
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value || "—"}</p>
    </div>
  </div>
);

// A minor's request waits on their guardian: the athlete can see it and nudge
// the guardian, but only the guardian (via the emailed link) can answer it.
const GuardianPendingNotice = ({ request, guardianEmail, onEditGuardian }) => {
  const resendMutation = useResendGuardianEmail();
  const [sentAt, setSentAt] = useState(null);
  const [resendError, setResendError] = useState("");
  const expiresAt = sentAt?.expiresAt || request.guardian_token_expires_at;

  const handleResend = async () => {
    setResendError("");
    try {
      const res = await resendMutation.mutateAsync({ requestId: request.id });
      setSentAt({ expiresAt: res?.expires_at });
    } catch (err) {
      setResendError(
        err?.code === "rate_limited"
          ? "Ya reenviamos el correo varias veces. Inténtalo de nuevo en un rato."
          : err?.message || "No pudimos reenviar el correo. Inténtalo de nuevo.",
      );
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-purple-200 bg-purple-50 p-4">
      <div className="flex items-start gap-2">
        <ShieldCheck size={18} className="mt-0.5 flex-shrink-0 text-purple-700" />
        <div className="text-sm text-purple-900">
          <p className="font-semibold">Esperando a tu tutor legal</p>
          <p className="mt-1">
            Como eres menor de edad, le enviamos esta solicitud a tu tutor
            {guardianEmail ? (
              <> (<span className="font-medium">{guardianEmail}</span>)</>
            ) : null}{" "}
            para que la apruebe o la rechace. Solo tu tutor puede responderla.
            {expiresAt && <> El enlace del correo vence el {formatDate(expiresAt)}.</>}
          </p>
        </div>
      </div>

      {sentAt && (
        <p className="mt-3 text-sm font-medium text-green-700">
          Listo, le reenviamos el correo a tu tutor.
        </p>
      )}
      {resendError && <p className="mt-3 text-sm text-red-600">{resendError}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleResend}
          disabled={resendMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-purple-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {resendMutation.isPending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Send size={16} />
          )}
          Reenviar correo a mi tutor
        </button>
        <button
          type="button"
          onClick={onEditGuardian}
          className="text-sm font-semibold text-purple-800 underline-offset-2 hover:underline"
        >
          ¿El correo de tu tutor está mal? Corrígelo en tu perfil
        </button>
      </div>
    </div>
  );
};

const AthleteContactRequestsPage = ({
  athleteId,
  athleteFullName,
  guardianEmail,
  onResolved,
}) => {
  const navigate = useNavigate();
  const [resolvedIds, setResolvedIds] = useState([]);
  const [respondingId, setRespondingId] = useState(null);
  const [error, setError] = useState("");
  const [reportTarget, setReportTarget] = useState(null);

  const requestsQuery = usePendingContactRequests({
    enabled: Boolean(athleteId),
  });
  const respondMutation = useRespondToContactRequest();

  const loading = requestsQuery.isLoading;
  const loadError = requestsQuery.isError
    ? requestsQuery.error?.message || "No se pudieron cargar las solicitudes."
    : "";
  // Quitar del listado pending las solicitudes ya resueltas en esta sesión
  // (la invalidación de react-query las elimina del servidor poco después).
  const requests = (requestsQuery.data || []).filter(
    (request) => !resolvedIds.includes(request.id),
  );

  const handleRespond = async (requestId, decision) => {
    if (respondingId) return;
    setRespondingId(requestId);
    setError("");
    try {
      // { updated: null } = ya fue respondida en otro lado; en ambos casos la
      // solicitud sale del listado pending, igual que antes.
      await respondMutation.mutateAsync({ requestId, decision });
      setResolvedIds((prev) => [...prev, requestId]);
      onResolved?.();
      if (decision === "approved") {
        navigate(`/athlete/messages/${requestId}`);
      }
    } catch (err) {
      setError(err.message || "No se pudo registrar la respuesta.");
    } finally {
      setRespondingId(null);
    }
  };

  return (
    <main className="flex-1 overflow-y-auto bg-gray-50">
      <div className="px-6 py-6 md:px-10 md:py-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Solicitudes de contacto
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Scouts que quieren conectar contigo. Acepta para compartir tus
            datos de contacto.
          </p>
        </div>

        {(error || loadError) && (
          <p className="mt-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error || loadError}
          </p>
        )}

        {/* List */}
        <div className="mt-8 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-blue-500" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Inbox size={40} className="text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">
                No tienes solicitudes pendientes
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Cuando un scout te envíe una solicitud, aparecerá aquí.
              </p>
            </div>
          ) : (
            requests.map((request) => {
              const scout = request.scout || {};
              const isResponding = respondingId === request.id;
              const awaitingGuardian = request.status === "guardian_pending";
              return (
                <div
                  key={request.id}
                  className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {scout.full_name || "Scout"}
                      </h3>
                      {scout.organization_name && (
                        <p className="text-sm text-gray-500">
                          {scout.organization_name}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">
                      {formatDate(request.created_at)}
                    </span>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <InfoLine
                      icon={Target}
                      label="Tipo de oportunidad"
                      value={request.opportunity_type}
                    />
                    <InfoLine
                      icon={MapPin}
                      label="Ubicación"
                      value={request.location}
                    />
                    <InfoLine
                      icon={User}
                      label="¿Qué le llamó la atención?"
                      value={request.attention_reason}
                    />
                    <InfoLine
                      icon={Building2}
                      label="¿Qué está buscando?"
                      value={request.looking_for}
                    />
                  </div>

                  {request.message && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg bg-gray-50 p-3">
                      <MessageSquare size={16} className="mt-0.5 flex-shrink-0 text-blue-950" />
                      <p className="text-sm text-gray-700 italic">{request.message}</p>
                    </div>
                  )}

                  {awaitingGuardian && (
                    <GuardianPendingNotice
                      request={request}
                      guardianEmail={guardianEmail}
                      onEditGuardian={() => navigate("/athlete/profile/personal")}
                    />
                  )}

                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    {!awaitingGuardian && (
                    <>
                    <button
                      type="button"
                      onClick={() => handleRespond(request.id, "approved")}
                      disabled={isResponding}
                      className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isResponding ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Check size={16} />
                      )}
                      Aceptar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRespond(request.id, "rejected")}
                      disabled={isResponding}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <X size={16} />
                      Rechazar
                    </button>
                    </>
                    )}
                    <button
                      type="button"
                      onClick={() => setReportTarget({
                        scoutId: request.scout_id,
                        scoutName: scout.full_name || "Scout",
                      })}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-red-600"
                      title="Reportar scout"
                    >
                      <Flag size={16} />
                      Reportar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <ReportModal
          isOpen={reportTarget !== null}
          onClose={() => setReportTarget(null)}
          reportedAthleteId={null}
          reportedScoutId={reportTarget?.scoutId || null}
          reportedName={reportTarget?.scoutName || ""}
          context="contact_request"
        />
      </div>
    </main>
  );
};

export default AthleteContactRequestsPage;
